import { Component, EventEmitter, Output, ViewEncapsulation } from '@angular/core';
import { MatSelectionListChange } from '@angular/material/list';
import { EnpsComponent } from '@common/components/base/enps-component';
import { FilterDef } from '@common/data-models/navigation/filter-def';
import { ViewStateFilterStates } from '@common/enums/viewstate-filter-states';
import { ViewStateFilterVisibility } from '@common/enums/viewstate-filter-visibility';
import { ViewStateLayouts } from '@common/enums/viewstate-layouts';
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { LanguageSettings } from '@common/services/language-settings';
import { LayoutState } from '@common/services/layout-state';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { Store } from '@ngrx/store';
import { ManageTeamMembersComponent } from '@src/suite/common/components/manage-team-members/manage-team-members';
import { PlanningViewsComponent } from '@src/suite/common/components/planning-views/planning-views';
import { FilterTypes } from '@src/suite/common/enums/filter-types';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import {
    selectIsSingleViewStateRecord,
    selectIsViewStateSortingManual,
    selectNavigationViewStateAllFilters,
    selectNavigationViewStateFilter,
    selectNavigationViewStateOptionalFilters,
    selectNavigationViewStateOptionalFiltersIDs,
    selectNavigationViewStateRequiredFilters,
    selectNavigationViewStateRequiredFiltersIDs,
    selectViewStateFilterLabel,
} from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { AppService } from '@suite/app.service';
import { map, Observable } from 'rxjs';

@Component({
    selector: 'st-filter',
    templateUrl: './filter.html',
    styleUrls: ['./filter.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-filter',
    },
})
export class FilterComponent extends EnpsComponent {
    @Output() public changeEmitter: EventEmitter<void> = new EventEmitter<void>();
    @Output() public closeFilter: EventEmitter<void> = new EventEmitter<void>();

    public viewStateTypes = ViewStateTypes;
    public viewStateFilterStates = ViewStateFilterStates;
    public viewStateLayouts = ViewStateLayouts;
    public viewStateFilterVisibility = ViewStateFilterVisibility;

    public PSTWebFirstX: string;

    public filterTypes = FilterTypes;
    public filterType: number = FilterTypes.REQUIRED;

    get isManageTeamMembers(): boolean {
        return this.navigationService.currentListView instanceof ManageTeamMembersComponent;
    }

    get isPlanningViews(): boolean {
        return this.navigationService.currentListView instanceof PlanningViewsComponent;
    }

    get isRTL() {
        return this.globalTablesService.getRtl();
    }

    viewStateFilter$ = this.store.select(selectNavigationViewStateFilter);
    viewStateAllFilters$ = this.store.select(selectNavigationViewStateAllFilters);
    viewStateRequiredFilters$ = this.store.select(selectNavigationViewStateRequiredFilters);
    viewStateRequiredFiltersIDs$ = this.store.select(selectNavigationViewStateRequiredFiltersIDs);
    viewStateOptionalFilters$ = this.store.select(selectNavigationViewStateOptionalFilters);
    viewStateOptionalFiltersIDs$ = this.store.select(selectNavigationViewStateOptionalFiltersIDs);
    isViewStateSortingManual$ = this.store.select(selectIsViewStateSortingManual);

    viewStateFilterLabel$ = this.store.select(selectViewStateFilterLabel);

    isSingleViewStateRecord$ = this.store.select(selectIsSingleViewStateRecord);

    constructor(
        public appService: AppService,
        public layoutState: LayoutState,
        public languageSettings: LanguageSettings,
        public nomWebServicesJson: NomWebServicesJson,
        public visualStateService: VisualStateService,
        public navigationService: NavigationService,
        public globalTablesService: GlobalTablesService,
        private store: Store
    ) {
        super();
        this.PSTWebFirstX = this.languageSettings.PSTWebFirstX;
    }

    toggle() {
        this.store.dispatch(ViewStateActions.toggleFilterState());
    }

    onFilterStateChange(event: MatSelectionListChange, fromRequired: boolean) {
        const selected = event.source.selectedOptions.selected.map(x => x.value);
        this.store.dispatch(ViewStateActions.addViewStateFilters({ required: fromRequired, filters: selected }));
    }

    isRequiredFilterSelected$(id: string): Observable<boolean> {
        return this.viewStateRequiredFiltersIDs$.pipe(
            map(filters => {
                return !!filters.find(f => f === id);
            })
        );
    }

    onRemoveFilter(item: FilterDef, type: FilterTypes) {
        this.store.dispatch(ViewStateActions.removeViewStateFilter({ required: type === FilterTypes.REQUIRED, filter: item.ID }));
    }

    onUpdateFilter() {
        this.store.dispatch(ViewStateActions.updateViewStateFilter());
    }

    toggleFilterElevation() {
        this.store.dispatch(ViewStateActions.toggleFilterElevation());
    }
}
